/** @jsx jsx */
/* @jsxFrag React.Fragment */

import { jsx, css } from '@emotion/core';
import React, { useState, useRef, useEffect } from 'react';
import getFieldLabel from 'helpers/getFieldLabel.js';

import { LabelAtom, DescriptionAtom } from './atoms.js';

const alignOptions = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end'
};

const DateTimeNative = ({
  form,
  field,
  theme,
  value,
  values,
  onChange,
  onPartialResponse,
  fieldsOptions,
  variables,
  urlParams,
  onClick,
  onAfterChange,
  disableDefaultCSS = false
}) => {
  if (!onClick) onClick = () => {};

  const valueObject = value.value;

  const stringTimeToObject = (value) => {
    if (!value) return {};

    let newValue = null;

    if (field.dateTimeHourMode === '12') {
      const [sHours, minutes] = value.match(/([0-9]{1,2}):([0-9]{2})/).slice(1);
      let hours = parseInt(sHours);
      let period = 'am';

      // Convert 24-hour to 12-hour format
      if (hours === 0) {
        hours = 12; // 00:00 → 12:00 AM
        period = 'am';
      } else if (hours === 12) {
        hours = 12; // 12:00 → 12:00 PM
        period = 'pm';
      } else if (hours > 12) {
        hours = hours - 12; // 13:00-23:00 → 1:00-11:00 PM
        period = 'pm';
      } else {
        // 1:00-11:00 → 1:00-11:00 AM
        period = 'am';
      }

      newValue = { hour: hours, minute: +minutes, ampm: period };
    } else if (field.dateTimeHourMode === '24') {
      const [hours, minutes] = value.split(':');
      newValue = { hour: hours, minute: minutes };
    }

    return newValue;
  };

  const stringDateToObject = (value) => {
    if (!value) return {};

    const day = value.split('-')[2];
    const month = value.split('-')[1];
    const year = value.split('-')[0];

    return { day, month, year };
  };

  // Format date with user-selected separator for display
  const formatDateForDisplay = (value) => {
    if (!value) {
      const separator = field.dateTimeDateSeparator || '-';
      // Parse the format to show correct placeholder
      let format = field.dateTimeFormat || 'day.month.year';
      if (format.includes(' ')) {
        format = format.split(' ')[0]; // Remove time part if present
      }
      const formatParts = format.split('.');
      const placeholder = formatParts
        .map((part) => {
          if (part === 'day') return 'dd';
          if (part === 'month') return 'mm';
          if (part === 'year') return 'yyyy';
          return part;
        })
        .join(separator);
      return placeholder;
    }

    try {
      const dateObj = JSON.parse(value);
      const separator = field.dateTimeDateSeparator || '-';
      let format = field.dateTimeFormat || 'day.month.year';
      if (format.includes(' ')) {
        format = format.split(' ')[0]; // Remove time part if present
      }
      const formatParts = format.split('.');

      // Only use date parts, ignore any time data
      const formattedParts = formatParts.map((part) => {
        if (part === 'day') return dateObj.day || '';
        if (part === 'month') return dateObj.month || '';
        if (part === 'year') return dateObj.year || '';
        return part;
      });

      return formattedParts.join(separator);
    } catch (e) {
      const separator = field.dateTimeDateSeparator || '-';
      let format = field.dateTimeFormat || 'day.month.year';
      if (format.includes(' ')) {
        format = format.split(' ')[0]; // Remove time part if present
      }
      const formatParts = format.split('.');
      const placeholder = formatParts
        .map((part) => {
          if (part === 'day') return 'dd';
          if (part === 'month') return 'mm';
          if (part === 'year') return 'yyyy';
          return part;
        })
        .join(separator);
      return placeholder;
    }
  };

  // Format time with user-selected format for display
  const formatTimeForDisplay = (value) => {
    if (!value) {
      return field.dateTimeHourMode === '12' ? '00:00am/pm' : '00:00';
    }

    try {
      const timeObj = JSON.parse(value);

      // Format time part
      let timePart = '';
      if (field.dateTimeHourMode === '12') {
        let hour = parseInt(timeObj.hour) || 0;
        const minute = timeObj.minute || '00';
        const ampm = timeObj.ampm || 'am';

        // Convert to 12-hour format properly
        if (hour === 0) {
          hour = 12; // 12:00 AM (midnight)
        } else if (hour > 12) {
          hour = hour - 12; // PM hours
        }
        // For 1-11 AM, keep as is

        timePart = `${hour}:${minute} ${ampm}`;
      } else {
        const hour = timeObj.hour || '00';
        const minute = timeObj.minute || '00';
        timePart = `${hour}:${minute}`;
      }

      return timePart;
    } catch (e) {
      return field.dateTimeHourMode === '12' ? '00:00am/pm' : '00:00';
    }
  };

  // Format datetime with user-selected separator for display
  const formatDateTimeForDisplay = (value) => {
    if (!value) {
      const separator = field.dateTimeDateSeparator || '-';
      // Parse the format to show correct placeholder
      const format = field.dateTimeFormat || 'day.month.year hour:minute';
      const formatParts = format.split(' ');
      const dateFormat = formatParts[0];
      const timeFormat = formatParts[1];

      const dateParts = dateFormat.split('.');
      const datePlaceholder = dateParts
        .map((part) => {
          if (part === 'day') return 'dd';
          if (part === 'month') return 'mm';
          if (part === 'year') return 'yyyy';
          return part;
        })
        .join(separator);

      const timePlaceholder = field.dateTimeHourMode === '12' ? '00:00am/pm' : '00:00';

      return `${datePlaceholder} ${timePlaceholder}`;
    }

    try {
      const dateObj = JSON.parse(value);
      const separator = field.dateTimeDateSeparator || '-';
      const format = field.dateTimeFormat || 'day.month.year hour:minute';
      const formatParts = format.split(' ');
      const dateFormat = formatParts[0];
      const timeFormat = formatParts[1];

      // Format date part
      const dateParts = dateFormat.split('.');
      const formattedDateParts = dateParts.map((part) => {
        if (part === 'day') return dateObj.day;
        if (part === 'month') return dateObj.month;
        if (part === 'year') return dateObj.year;
        return part;
      });
      const datePart = formattedDateParts.join(separator);

      // Format time part
      let timePart = '';
      if (field.dateTimeHourMode === '12') {
        let hour = parseInt(dateObj.hour) || 0;
        const minute = dateObj.minute || '00';
        const ampm = dateObj.ampm || 'am';

        // Convert to 12-hour format properly
        if (hour === 0) {
          hour = 12; // 12:00 AM (midnight)
        } else if (hour > 12) {
          hour = hour - 12; // PM hours
        }
        // For 1-11 AM, keep as is

        timePart = `${hour}:${minute} ${ampm}`;
      } else {
        const hour = dateObj.hour || '00';
        const minute = dateObj.minute || '00';
        timePart = `${hour}:${minute}`;
      }

      return `${datePart} ${timePart}`;
    } catch (e) {
      const separator = field.dateTimeDateSeparator || '-';
      const format = field.dateTimeFormat || 'day.month.year hour:minute';
      const formatParts = format.split(' ');
      const dateFormat = formatParts[0];
      const timeFormat = formatParts[1];

      const dateParts = dateFormat.split('.');
      const datePlaceholder = dateParts
        .map((part) => {
          if (part === 'day') return 'dd';
          if (part === 'month') return 'mm';
          if (part === 'year') return 'yyyy';
          return part;
        })
        .join(separator);

      const timePlaceholder = field.dateTimeHourMode === '12' ? '00:00am/pm' : '00:00';

      return `${datePlaceholder} ${timePlaceholder}`;
    }
  };

  const inputContainerStyleTypes = {
    style1: {
      borderBottom: `${theme.inputFieldsIdleBorderSize}px solid ${theme.inputFieldsIdleBorderColor}`
    },
    style2: {
      border: 'none'
    },
    style3: {
      border: `${theme.inputFieldsIdleBorderSize}px solid ${theme.inputFieldsIdleBorderColor}`
    }
  };

  const mainStyle = css({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: alignOptions[theme.inputFieldsDropdownsAlignment],
    margin: `${theme.inputFieldsDropdownsTopMargin || 0}px 0 ${theme.inputFieldsDropdownsBottomMargin || 0}px 0`
  });

  const contentStyle = css({
    width: '100%',
    maxWidth: `${theme.inputFieldsDropdownsWidth}${theme.inputFieldsDropdownsWidthType}`
  });

  const inputContainerStyle = css({
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    cursor: 'text',
    fontFamily: theme.typographyContentFontFamily,
    background: theme.inputFieldsIdleBackground,
    padding: `${theme.inputFieldsIdleVerticalPadding || 0}px ${theme.inputFieldsIdleHorizontalPadding || 0}px`,
    color: theme.inputFieldsIdleTextColor,
    fontWeight: theme.inputFieldsIdleTextWeight,
    fontSize: theme.inputFieldsIdleTextFontSize,
    borderRadius: `${theme.inputFieldsIdleRoundness}px`,
    boxShadow: theme.inputFieldsIdleShadow
      ? `${theme.inputFieldsIdleShadowOffsetX}px ${theme.inputFieldsIdleShadowOffsetY}px ${theme.inputFieldsIdleShadowBlur}px ${theme.inputFieldsIdleShadowSpread}px ${theme.inputFieldsIdleShadowColor}`
      : 'none',
    ...inputContainerStyleTypes[theme.inputFieldsDropdownsType],
    '& input': {
      padding: '0px',
      margin: '0px',
      width: '100%',
      color: 'transparent',
      fontWeight: theme.inputFieldsIdleTextWeight,
      fontSize: theme.inputFieldsIdleTextFontSize,
      lineHeight: '142%'
    },
    '& input::placeholder': {
      color: theme.inputFieldsIdlePlaceholderColor
    },
    '& input::-webkit-calendar-picker-indicator': {
      background: 'transparent',
      bottom: 0,
      color: 'transparent',
      cursor: 'pointer',
      height: 'auto',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: 'auto'
    },
    '&:hover': (() => {
      if (!theme.inputFieldsHoverEnable) return {};

      const obj = {
        background: theme.inputFieldsHoverBackground,
        borderColor: theme.inputFieldsHoverBorderColor,
        color: theme.inputFieldsHoverTextColor,
        '& input': {
          color: 'transparent'
        },
        '& input::placeholder': {
          color: theme.inputFieldsHoverPlaceholderColor
        }
      };

      if (theme.inputFieldsHoverShadow) {
        obj.boxShadow = `${theme.inputFieldsHoverShadowOffsetX}px ${theme.inputFieldsHoverShadowOffsetY}px ${theme.inputFieldsHoverShadowBlur}px ${theme.inputFieldsHoverShadowSpread}px ${theme.inputFieldsHoverShadowColor}`;
      } else {
        obj.boxShadow = 'none';
      }

      return obj;
    })(),
    '&.isActive': (() => {
      if (!theme.inputFieldsActiveEnable) return {};

      const obj = {
        background: theme.inputFieldsActiveBackground,
        borderColor: theme.inputFieldsActiveBorderColor,
        color: theme.inputFieldsActiveTextColor,
        '& input': {
          color: 'transparent'
        },
        '& input::placeholder': {
          color: theme.inputFieldsActivePlaceholderColor
        }
      };

      if (theme.inputFieldsActiveShadow) {
        obj.boxShadow = `${theme.inputFieldsActiveShadowOffsetX}px ${theme.inputFieldsActiveShadowOffsetY}px ${theme.inputFieldsActiveShadowBlur}px ${theme.inputFieldsActiveShadowSpread}px ${theme.inputFieldsActiveShadowColor}`;
      } else {
        obj.boxShadow = 'none';
      }

      return obj;
    })()
  });

  // Style for the display overlay
  const displayOverlayStyle = css({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.inputFieldsIdleVerticalPadding || 0}px ${theme.inputFieldsIdleHorizontalPadding || 0}px`,
    pointerEvents: 'none',
    fontWeight: theme.inputFieldsIdleTextWeight,
    fontSize: theme.inputFieldsIdleTextFontSize,
    lineHeight: '142%',
    zIndex: 1
  });

  const [active, setActive] = useState(false);

  // Time ->
  const handleTimeChange = (e) => {
    const newValue = stringTimeToObject(e.target.value);

    onChange({ [field._id]: JSON.stringify(newValue) }, true);
    if (onAfterChange) onAfterChange({ [field._id]: JSON.stringify(newValue) }, true);
  };

  const parseTimeValue = (value) => {
    if (!value) return '';

    let newValue = '';

    value = JSON.parse(value);

    if (field.dateTimeHourMode === '12') {
      let hour = parseInt(value.hour);
      let minute = String(value.minute);

      // Convert 12-hour to 24-hour format for native input
      if (value.ampm === 'pm' && hour !== 12) {
        hour = hour + 12; // 1-11 PM → 13-23
      } else if (value.ampm === 'am' && hour === 12) {
        hour = 0; // 12 AM → 00
      }

      // Pad with zeros
      hour = hour.toString().padStart(2, '0');
      minute = minute.padStart(2, '0');

      newValue = `${hour}:${minute}`;
    } else if (field.dateTimeHourMode === '24') {
      newValue = `${value.hour}:${value.minute}`;
    }

    return newValue;
  };
  // <- Time

  // Date ->
  const handleDateChange = (e) => {
    const newValue = stringDateToObject(e.target.value);

    onChange({ [field._id]: JSON.stringify(newValue) }, true);
    if (onAfterChange) onAfterChange({ [field._id]: JSON.stringify(newValue) }, true);
  };

  const parseDateValue = (value) => {
    if (!value) return '';

    value = JSON.parse(value);

    return `${value.year}-${value.month}-${value.day}`;
  };
  // <- Date

  // Date time ->
  const handleDateTimeChange = (e) => {
    const time = e.target.value.split('T')[1];
    const date = e.target.value.split('T')[0];

    const timeObject = stringTimeToObject(time);
    const dateObject = stringDateToObject(date);

    const newValue = { ...timeObject, ...dateObject };

    onChange({ [field._id]: JSON.stringify(newValue) }, true);
    if (onAfterChange) onAfterChange({ [field._id]: JSON.stringify(newValue) }, true);
  };

  const parseDateTimeValue = (value) => {
    if (!value) return '';

    return `${parseDateValue(value)}T${parseTimeValue(value)}`;
  };
  // <- Date time

  const blurFocusProps = {
    onFocus: () => {
      setActive(true);
    },
    onBlur: () => {
      setActive(false);
      onPartialResponse(field._id);
    }
  };

  return (
    <>
      <div css={disableDefaultCSS ? css({}) : mainStyle} onClick={() => onClick(field._id)}>
        <div css={disableDefaultCSS ? css({}) : contentStyle}>
          <LabelAtom required={field.required} error={field.error} theme={theme} disableDefaultCSS={disableDefaultCSS}>
            {getFieldLabel(field.label, values, 'jsx', fieldsOptions, variables, urlParams)}
          </LabelAtom>

          <div css={disableDefaultCSS ? css({}) : inputContainerStyle} className={active ? 'isActive' : ''}>
            {field.dateTimeMode === 'datetime' && (
              <>
                <input type="datetime-local" value={parseDateTimeValue(valueObject)} onChange={handleDateTimeChange} {...blurFocusProps} />
                <div
                  css={disableDefaultCSS ? css({}) : displayOverlayStyle}
                  style={
                    disableDefaultCSS
                      ? css({})
                      : { color: valueObject ? theme.inputFieldsIdleTextColor : theme.inputFieldsIdlePlaceholderColor }
                  }>
                  {formatDateTimeForDisplay(valueObject)}
                </div>
              </>
            )}
            {field.dateTimeMode === 'date' && (
              <>
                <input type="date" value={parseDateValue(valueObject)} onChange={handleDateChange} {...blurFocusProps} />
                <div
                  css={disableDefaultCSS ? css({}) : displayOverlayStyle}
                  style={
                    disableDefaultCSS
                      ? css({})
                      : { color: valueObject ? theme.inputFieldsIdleTextColor : theme.inputFieldsIdlePlaceholderColor }
                  }>
                  {formatDateForDisplay(valueObject)}
                </div>
              </>
            )}
            {field.dateTimeMode === 'time' && (
              <>
                <input type="time" value={parseTimeValue(valueObject)} onChange={handleTimeChange} {...blurFocusProps} />
                <div
                  css={disableDefaultCSS ? css({}) : displayOverlayStyle}
                  style={
                    disableDefaultCSS
                      ? css({})
                      : { color: valueObject ? theme.inputFieldsIdleTextColor : theme.inputFieldsIdlePlaceholderColor }
                  }>
                  {formatTimeForDisplay(valueObject)}
                </div>
              </>
            )}
          </div>

          <DescriptionAtom theme={theme} disableDefaultCSS={disableDefaultCSS}>
            {getFieldLabel(field.description, values, 'jsx', fieldsOptions, variables, urlParams)}
          </DescriptionAtom>
        </div>
      </div>
    </>
  );
};

export default DateTimeNative;
